"use strict";
/**
 *
 * usePlumbState
 *
 * React hooks for handling Plumbs.
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const fast_equals_1 = require("fast-equals");
const PlumbContext_1 = require("./PlumbContext");
const useForceUpdate_1 = require("./useForceUpdate");
function usePlumbState(props = {}) {
    // NOTE: plumb is either provided or not
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { plumb = PlumbContext_1.usePlumbContext(), stateIsEqual } = props;
    // we prefer using ref + forceUpdate to guarantee latest value delivery
    const forceUpdate = useForceUpdate_1.useForceUpdate();
    const valueRef = react_1.useRef({
        state: plumb.state,
        lastTag: plumb.lastTag,
    });
    react_1.useLayoutEffect(() => {
        const maybeNewPlumbState = plumb.state;
        if (valueRef.current.state !== maybeNewPlumbState) {
            valueRef.current = { state: maybeNewPlumbState, lastTag: plumb.lastTag };
            forceUpdate();
        }
    }, [plumb]);
    react_1.useLayoutEffect(() => {
        const subscription = plumb.subscribe((nextState, nextTag) => {
            if (!(stateIsEqual || fast_equals_1.shallowEqual)(valueRef.current.state, nextState)) {
                valueRef.current = { state: nextState, lastTag: nextTag };
                forceUpdate();
            }
        });
        return () => {
            subscription.dispose();
        };
    }, [plumb]);
    return [valueRef.current.state, valueRef.current.lastTag];
}
exports.usePlumbState = usePlumbState;
function PlumbState(props) {
    const { children } = props, rest = __rest(props, ["children"]);
    const [state, lastTag] = usePlumbState(rest);
    return children(state, lastTag);
}
exports.PlumbState = PlumbState;
function useMappedPlumbState(props) {
    // NOTE: plumb is either provided or not
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { plumb = PlumbContext_1.usePlumbContext(), mapper, stateIsEqual } = props;
    // we prefer using ref + forceUpdate to guarantee latest value delivery
    const forceUpdate = useForceUpdate_1.useForceUpdate();
    const valueRef = react_1.useRef({
        state: mapper(plumb.state),
        lastTag: plumb.lastTag,
    });
    const initRef = react_1.useRef(true);
    react_1.useLayoutEffect(() => {
        if (!initRef.current) {
            const maybeNewPlumbState = mapper(plumb.state);
            if (valueRef.current.state !== maybeNewPlumbState) {
                valueRef.current = { state: maybeNewPlumbState, lastTag: plumb.lastTag };
                forceUpdate();
            }
        }
        initRef.current = false;
    }, [plumb]);
    react_1.useLayoutEffect(() => {
        const subscription = plumb.subscribe((nextState, nextTag) => {
            const mappedState = mapper(nextState);
            if (!(stateIsEqual || fast_equals_1.shallowEqual)(valueRef.current.state, mappedState)) {
                valueRef.current = { state: mappedState, lastTag: nextTag };
                forceUpdate();
            }
        });
        return () => {
            subscription.dispose();
        };
    }, [plumb, mapper]);
    return [valueRef.current.state, valueRef.current.lastTag];
}
exports.useMappedPlumbState = useMappedPlumbState;
function MappedPlumbState(props) {
    const { children } = props, rest = __rest(props, ["children"]);
    const [state, lastTag] = useMappedPlumbState(rest);
    return children(state, lastTag);
}
exports.MappedPlumbState = MappedPlumbState;
